import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #ffffff;
  margin-bottom: 2rem;
  text-align: center;
`;

const Content = styled.div`
  color: #cccccc;
  line-height: 1.6;
  font-size: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  color: #ffffff;
  margin-top: 2rem;
  margin-bottom: 0.75rem;
`;

const Small = styled.small`
  color: #aaaaaa;
`;

const List = styled.ul`
  padding-left: 1.25rem;
`;

const Privacy: React.FC = () => {
  return (
    <Container>
      <Title>Privacy Policy</Title>
      <Content>
        <p>
        Last updated: <Small>August 21, 2025</Small>
        </p>

        <p>
          This Privacy Policy explains how <strong>YouMeditation</strong> ("the Service"), owned and operated by <strong>TAF Technology Inc.</strong> ("Company", "we", "us", or "our"), collects, uses, discloses, and safeguards your information when you use the Service.
        </p>


        <SectionTitle>Information we collect</SectionTitle>
        <p>We collect only the minimum information needed to provide and improve the Service:</p>
        <List>
          <li>
            Account information: your name, email address, password (hashed), and basic profile details.
          </li>
          <li>
            Payment information: processed by our payment provider (Stripe). We do not store full credit card numbers. See Stripe’s privacy notice for details.
          </li>
          <li>
            Content you provide: meditation content, comments, audio uploads, and settings you choose to save (stored with our cloud storage provider, Amazon Web Services).
          </li>
          <li>
            AI features content: text you input and audio you upload (including voice/speech) that you ask us to process using AI features.
          </li>
          <li>
            Usage and device data: log data, IP address, browser type, device identifiers, and interactions with the Service. We use Google Analytics for aggregated analytics.
          </li>
          <li>
            Cookies and similar technologies: used for essential functionality, preferences, and analytics.
          </li>
        </List>

        <SectionTitle>How we use your information</SectionTitle>
        <List>
          <li>Provide, maintain, and improve the Service.</li>
          <li>Process payments and manage subscriptions.</li>
          <li>Understand usage to improve features and performance.</li>
          <li>Communicate with you about your account and Service updates.</li>
          <li>Protect against fraud, abuse, and security incidents.</li>
          <li>Comply with legal obligations.</li>
        </List>

        <SectionTitle>Legal bases (EEA/UK users)</SectionTitle>
        <List>
          <li>Performance of a contract (to provide the Service you requested).</li>
          <li>Legitimate interests (e.g., improving and securing the Service).</li>
          <li>Consent (for non-essential cookies/analytics where required).</li>
          <li>Compliance with legal obligations.</li>
        </List>

        <SectionTitle>How we share information</SectionTitle>
        <List>
          <li>
            Service providers: We share information with trusted vendors who help us operate the Service, including payment processing (Stripe), analytics (Google), infrastructure and file storage (Amazon Web Services such as Amazon S3/CloudFront), email delivery (Amazon SES), and AI services (e.g., OpenAI and Replicate). When you use AI features, the text and audio (including voice/speech) you submit may be transmitted to these AI providers to generate outputs, subject to their own privacy policies and terms. These providers may process information on our behalf under appropriate agreements.
          </li>
          <li>
            Legal, safety, and compliance: We may disclose information if required by law or to protect the rights, safety, and security of users, the public, or the Company.
          </li>
          <li>
            Business transfers: In the event of a merger, acquisition, or asset sale, your information may be transferred as permitted by law.
          </li>
        </List>
        <p>
          We do <strong>not</strong> sell your email address. We do not provide your email address to third parties for their own marketing.
        </p>

        <SectionTitle>Payments with Stripe</SectionTitle>
        <p>
          We use Stripe to process payments. Stripe collects and processes payment information subject to its own privacy policy. Learn more at{' '}
          <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a>.
        </p>

        <SectionTitle>Analytics with Google Analytics</SectionTitle>
        <p>
          We use Google Analytics to understand aggregate usage and improve the Service. By default, analytics cookies are <strong>disabled</strong> using Google Consent Mode and will only be enabled if you opt in via our cookie banner. When enabled, we configure IP anonymization and respect your consent preferences. Learn more at{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a> and opt out using the Google Analytics Opt-out Browser Add-on at{' '}
          <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">tools.google.com/dlpage/gaoptout</a>.
        </p>

        <SectionTitle>Cookies and similar technologies</SectionTitle>
        <p>We use the following categories of cookies:</p>
        <List>
          <li>
            <strong>Essential cookies</strong> (strictly necessary): Required to operate the Service (e.g., security, session management, load balancing, and preferences). These are always active and do not require consent.
          </li>
          <li>
            <strong>Analytics cookies</strong> (non-essential): Help us measure and improve the Service. These are only set if you consent via our cookie banner.
          </li>
        </List>
        <p>
          Cookie banner choices: Our banner presents two options. Selecting <em>Accept Terms of Service, Privacy Policy, and essential Cookies</em> keeps analytics disabled (essential only). Selecting <em>Accept Terms of Service, Privacy Policy, and recommended Cookies</em> enables analytics cookies. Your choice is stored in your browser’s local storage under the key <code>ym_cookie_consent_v1</code> and applies per browser and device. You can withdraw or change your consent at any time by clearing your cookies/local storage for our site or contacting us; after clearing, the banner will reappear.
        </p>
        <p>
          Consent records: When you submit a choice, we also log a server-side consent record for audit and compliance. This record includes (where available) your user ID (if you are logged in), your full IP address, your browser user agent, the choice made (e.g., cookies accepted/rejected and acceptance of our Terms and Privacy), the policy version, and a timestamp. This information is derived from your request on our servers and used solely for record-keeping, security, and legal compliance.
        </p>

        <SectionTitle>Data retention</SectionTitle>
        <p>
          We retain information for as long as necessary to fulfill the purposes described in this Policy, including to provide the Service, comply with legal obligations, resolve disputes, and enforce agreements. Specific retention periods may vary based on context and legal requirements.
        </p>

        <SectionTitle>Security</SectionTitle>
        <p>
          We implement reasonable technical and organizational measures to protect information. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.
        </p>

        <SectionTitle>International data transfers</SectionTitle>
        <p>
          We operate from the United States (North Carolina). If you access the Service from outside the U.S., your information may be transferred to, stored, and processed in the U.S. and other countries where our service providers operate (including Amazon Web Services and AI service providers such as OpenAI and Replicate). We take steps to protect transfers as required by applicable law.
        </p>

        <SectionTitle>Your rights and choices</SectionTitle>
        <List>
          <li>Access, update, or correct your account information.</li>
          <li>Delete your account or request deletion of certain information, subject to legal obligations.</li>
          <li>Opt out of marketing emails by using unsubscribe links or contacting us.</li>
          <li>Manage cookies via browser settings; withdraw consent where applicable.</li>
          <li>EEA/UK users: You may have additional rights (restriction, portability, objection). You may also complain to your local data protection authority.</li>
        </List>
        
        <SectionTitle>Children’s privacy</SectionTitle>
        <p>
          The Service is not directed to children under 13, and we do not knowingly collect personal information from children under 13. If you believe a child has provided personal information, please contact us so we can take appropriate action.
        </p>

        <SectionTitle>Third-party links and services</SectionTitle>
        <p>
          The Service may contain links to third-party websites or services. We are not responsible for the privacy practices of those third parties.
        </p>

        <SectionTitle>Changes to this Privacy Policy</SectionTitle>
        <p>
          We may update this Policy from time to time. If changes materially affect your rights, we will provide notice by email or in-app notice. The updated Policy will be effective on the date indicated above.
        </p>

        <SectionTitle>Contact</SectionTitle>
        <p>
          If you have questions about this Privacy Policy or our practices, contact us at <strong>support@youmeditation.com</strong>.
        </p>
      </Content>
    </Container>
  );
};

export default Privacy;